import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTicket } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { uploadService } from '../../services/upload.service';
import { sessionService } from '../../services/session.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import TermsConsentRow from '../../components/legal/TermsConsentRow';
import { HUMAN_REVIEW_VERSION, TERMS_VERSION } from '../../constants/legal';
import { runGates } from '../../gating/gateEngine';
import { handleGateDecision } from '../../gating/gateHandler';
import { buildGateContext } from '../../gating/buildGateContext';
import { AuthGate, SubscriptionGate } from '../../gating/gates';
import { debugInfo, debugWarn } from '../../utils/debug';

const START_MAX_FILE_SIZE = 20 * 1024 * 1024;
const START_ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
];

const buildSchema = (t: (k: string) => string) =>
  yup
    .object({
      type: yup
        .string()
        .oneOf(['parking', 'traffic', 'speeding', 'red_light', 'other'])
        .required(t('tickets:upload.types.label')),
      description: yup.string().optional().max(100, 'max'),
      location: yup.string().optional().max(100, 'max'),
      dateIssued: yup.string().optional(),
    })
    .required();

type TicketUploadFormData = {
  type: 'parking' | 'traffic' | 'speeding' | 'red_light' | 'other';
  description?: string;
  location?: string;
  dateIssued?: string;
};

const TicketUploadPage: React.FC = () => {
  const { user } = useAuth();
  const { setSessionFromTicketResponse, setUploadProgress } = useTicket();
  const navigate = useNavigate();
  const location = useLocation();
  const { appealSessionId } = useParams<{ appealSessionId?: string }>();
  const { t } = useTranslation(['tickets']);
  const sessionFields = (location.state as any)?.fields;
  const sessionTicket = (location.state as any)?.ticket;
  
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [startingExtraction, setStartingExtraction] = useState(false);
  const [billingActive, setBillingActive] = useState<boolean | null>(null);
  const [billingRefreshing, setBillingRefreshing] = useState(false);
  const [billingOverCap, setBillingOverCap] = useState<boolean | null>(null);
  const [billingCapUsd, setBillingCapUsd] = useState<number | null>(null);
  const [billingCostUsd, setBillingCostUsd] = useState<number | null>(null);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const startDisabledReason = useMemo(() => {
    if (startingExtraction) return t('upload.progress.starting');
    if (billingOverCap) return 'You’ve reached your $5 usage cap. Please contact support.';
    if (!uploadedFile && !appealSessionId) return t('upload.errors.missingFile');
    if (!termsChecked) return 'Please accept the terms to continue.';
    return '';
  }, [appealSessionId, billingOverCap, startingExtraction, t, termsChecked, uploadedFile]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    resolver: yupResolver(buildSchema(t)),
    defaultValues: {
      type: 'parking' as const
    }
  });

  // Check if file was passed from landing page
  useEffect(() => {
    const stateFile = location.state?.file;
    if (stateFile) {
      setUploadedFile(stateFile);
      createPreview(stateFile);
    }
  }, [location.state]);

  const stripeStatus = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get('stripe');
  }, [location.search]);

  const gateReturnUrl = useMemo(() => {
    if (appealSessionId) {
      return `/ticket/${appealSessionId}/evidence`;
    }
    return `${location.pathname}${location.search || ''}`;
  }, [appealSessionId, location.pathname, location.search]);

  const refreshBilling = useCallback(
    async (cacheBust: boolean) => {
      setBillingRefreshing(true);
      try {
        const response = await sessionService.getBillingMe(cacheBust);
        const canGenerate = response?.entitlements?.canGenerateLetters ?? response?.active;
        setBillingActive(!!canGenerate);
        setBillingOverCap(!!response?.isOverCap);
        setBillingCapUsd(
          typeof response?.capUsd === 'number' ? response.capUsd : null
        );
        setBillingCostUsd(
          typeof response?.costUsdTotal === 'number' ? response.costUsdTotal : null
        );
      } catch (error) {
        debugWarn('Billing refresh failed', error);
        setBillingActive(null);
        setBillingOverCap(null);
        setBillingCapUsd(null);
        setBillingCostUsd(null);
      } finally {
        setBillingRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    if (stripeStatus === 'success') {
      toast.success('Subscription active. You can continue.');
      refreshBilling(true);
    }
    if (stripeStatus === 'cancel') {
      toast.info('Checkout canceled.');
    }
  }, [stripeStatus, refreshBilling]);

  useEffect(() => {
    refreshBilling(false);
  }, [refreshBilling]);

  useEffect(() => {
    let active = true;
    const checkSubscriptionGate = async () => {
      const ctx = buildGateContext({
        user,
        returnUrl: gateReturnUrl,
        refreshBilling: () => sessionService.getBillingMe(true),
      });
      const decision = await runGates([AuthGate, SubscriptionGate], ctx);
      if (!active) return;
      handleGateDecision(decision, {
        navigate,
        toast: (message) => toast.error(message),
        returnUrl: gateReturnUrl,
      });
    };

    void checkSubscriptionGate();

    return () => {
      active = false;
    };
  }, [gateReturnUrl, navigate, user]);

  useEffect(() => {
    if (!appealSessionId) return;
    let active = true;
    const loadTerms = async () => {
      try {
        const response = await sessionService.getAppealSession(appealSessionId);
        if (!active) return;
        const accepted =
          response?.termsAcceptedAt &&
          response?.termsAcceptedVersion === TERMS_VERSION;
        setTermsChecked(!!accepted);
        const storedHumanReview = localStorage.getItem('humanReviewAcceptedVersion');
        if (storedHumanReview === HUMAN_REVIEW_VERSION) {
          await sessionService.acceptHumanReviewDisclaimer(appealSessionId, {
            version: HUMAN_REVIEW_VERSION,
          });
          localStorage.removeItem('humanReviewAcceptedVersion');
        }
      } catch (error) {
        // ignore load errors
      }
    };
    loadTerms();
    return () => {
      active = false;
    };
  }, [appealSessionId]);

  useEffect(() => {
    if (!appealSessionId || !termsChecked || termsSaving) return;
    setTermsSaving(true);
    sessionService
      .acceptSessionTerms(appealSessionId, { version: TERMS_VERSION })
      .catch(() => {
        toast.error('Unable to save terms consent.');
        setTermsChecked(false);
      })
      .finally(() => {
        setTermsSaving(false);
      });
    // TODO: analytics event for terms consent accepted.
  }, [appealSessionId, termsChecked, termsSaving]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    maxSize: START_MAX_FILE_SIZE,
    disabled: billingOverCap === true,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setUploadedFile(file);
        createPreview(file);
      }
    },
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0];
      if (rejection.errors[0].code === 'file-too-large') {
        toast.error(t('upload.errors.tooLarge'));
      } else if (rejection.errors[0].code === 'file-invalid-type') {
        toast.error(t('upload.errors.invalidType'));
      }
    }
  });

  const createPreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const onSubmit: SubmitHandler<TicketUploadFormData> = async (data) => {
    if (!uploadedFile) {
      toast.error(t('upload.errors.missingFile'));
      return;
    }
    if (!termsChecked) {
      toast.error('Please accept the terms to continue.');
      return;
    }
    if (billingOverCap) {
      toast.error('You’ve reached your $5 usage cap. Please contact support.');
      return;
    }

    const sanitizeFreeText = (value?: string) => {
      if (!value) return '';
      let text = String(value);
      text = text.replace(/https?:\/\/\S+/gi, '');
      text = text.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '');
      text = text.replace(/\+?\d[\d\s().-]{7,}\d/g, '');
      text = text.replace(/<[^>]*>/g, '');
      text = text.replace(/[*_`~>#]/g, '');
      text = text.replace(/[!?.]{4,}/g, (match) => match.slice(0, 3));
      text = text.replace(/[ \t]{2,}/g, ' ');
      text = text.replace(/\n{3,}/g, '\n\n');
      return text.trim().slice(0, 100);
    };

    const sanitizedData = {
      ...data,
      description: sanitizeFreeText(data.description),
      location: sanitizeFreeText(data.location),
    };

    setUploadProgress({ progress: 0, status: 'uploading', message: t('upload.progress.uploading') });

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90)
        }));
      }, 200);

      const response = await uploadService.uploadTicket(uploadedFile, sanitizedData);
      
      clearInterval(progressInterval);
      
      if (response.success && response.data) {
        setUploadProgress({ progress: 100, status: 'completed', message: t('upload.progress.completed') });
        setSessionFromTicketResponse({ ticket: response.data });
        toast.success(t('upload.toast.success'));
        
        // Navigate to OCR results page
        setTimeout(() => {
          navigate(`/ticket/${response.data!.id}/review`);
        }, 1000);
      } else {
        throw new Error(response.message || t('upload.toast.uploadFailed'));
      }
    } catch (error: any) {
      setUploadProgress({ progress: 0, status: 'error', message: error.message || t('upload.toast.uploadFailed') });
      toast.error(error.message || t('upload.toast.failed'));
    } finally {
    }
  };

  const isValidStartFile = (file: File) => {
    return START_ALLOWED_MIME_TYPES.includes(file.type) && file.size <= START_MAX_FILE_SIZE;
  };

  const resolveAppealSessionId = (payload?: {
    appealSessionId?: string;
    ticketId?: string;
    ticket?: { id?: string };
  }): string | null => {
    return payload?.appealSessionId || payload?.ticketId || payload?.ticket?.id || null;
  };

  const handleStart = async () => {
    if (startingExtraction) {
      return;
    }
    if (billingOverCap) {
      toast.error('You’ve reached your $5 usage cap. Please contact support.');
      return;
    }
    if (!termsChecked) {
      toast.error('Please accept the terms to continue.');
      return;
    }
    debugInfo('Upload Start clicked', { appealSessionId, routePath: location.pathname });
    if (!uploadedFile) {
      if (appealSessionId) {
        if (sessionFields || sessionTicket) {
          debugInfo('Navigating to review with cached session state', {
            appealSessionId,
            hasFields: !!sessionFields,
            hasTicket: !!sessionTicket,
          });
          navigate(`/ticket/${appealSessionId}/review`, { state: { ticket: sessionTicket, fields: sessionFields } });
          return;
        }

        try {
          const resp = await sessionService.getTicket(appealSessionId);
          debugInfo('Loaded ticket for review', { appealSessionId, success: resp.success });
          if (resp.success && resp.data) {
            navigate(`/ticket/${appealSessionId}/review`, { state: { ticket: resp.data.ticket, fields: resp.data.fields } });
            return;
          }
          throw new Error(resp.message || t('upload.toast.failed'));
        } catch (error: any) {
          toast.error(error.message || t('upload.toast.failed'));
          return;
        }
      }

      toast.error(t('upload.errors.missingFile'));
      return;
    }

    if (!isValidStartFile(uploadedFile)) {
      toast.error(t('upload.errors.startFileInvalid'));
      return;
    }

    {
      const ctx = buildGateContext({
        user,
        returnUrl: gateReturnUrl,
        refreshBilling: () => sessionService.getBillingMe(true),
      });
      const decision = await runGates([AuthGate, SubscriptionGate], ctx);
      const allowed = handleGateDecision(decision, {
        navigate,
        toast: (message) => toast.error(message),
        returnUrl: gateReturnUrl,
      });
      if (!allowed) {
        return;
      }
    }

    setStartingExtraction(true);
    setUploadProgress({ progress: 0, status: 'processing', message: t('upload.progress.starting') });

    try {
      const response = await uploadService.startTicketExtraction(uploadedFile);

      if (response.success && response.data) {
        debugInfo('Extraction response received', {
          hasTicket: !!response.data.ticket,
          ticketId: response.data.ticketId,
          appealSessionId: response.data.appealSessionId,
        });
        if (response.data.ticket) {
          setSessionFromTicketResponse({
            ticket: response.data.ticket as any,
            fields: response.data.fields,
            ticketId: response.data.ticketId,
          });
        }
        const sessionId = resolveAppealSessionId(response.data);
        if (sessionId && termsChecked) {
          try {
            await sessionService.acceptSessionTerms(sessionId, { version: TERMS_VERSION });
          } catch (error) {
            toast.error('Unable to save terms consent.');
          }
        }
        setUploadProgress({ progress: 100, status: 'completed', message: t('upload.toast.startSuccess') });
        toast.info(t('upload.toast.startSuccess'));
        debugInfo('Appeal session resolved', { appealSessionId: sessionId });
        if (!sessionId) {
          throw new Error('Unable to start appeal session.');
        }
        if (termsChecked) {
          try {
            await sessionService.acceptSessionTerms(sessionId, { version: TERMS_VERSION });
          } catch (error) {
            toast.error('Unable to save terms consent.');
          }
        }
        try {
          await sessionService.getAppealSession(sessionId);
        } catch (error) {
          debugWarn('Failed to create appeal session', error);
          toast.error('Unable to create appeal session.');
          return;
        }

        debugInfo('Navigating to evidence after extraction', { sessionId });
        navigate(`/ticket/${sessionId}/evidence`, {
          state: { ticket: response.data.ticket, fields: response.data.fields },
        });
        return;
      } else {
        throw new Error(response.message || t('upload.toast.failed'));
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'BILLING_CAP_REACHED') {
        setBillingOverCap(true);
        toast.error('You’ve reached your $5 usage cap. Please contact support.');
        setUploadProgress({ progress: 0, status: 'error', message: t('upload.toast.failed') });
        return;
      }
      setUploadProgress({ progress: 0, status: 'error', message: error.message || t('upload.toast.failed') });
      toast.error(error.message || t('upload.toast.failed'));
    } finally {
      setStartingExtraction(false);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const ticketTypes = [
    { value: 'parking', label: t('upload.types.parking'), icon: '🅿️', supported: true },
    { value: 'traffic', label: t('upload.types.traffic'), icon: '🚦', supported: false },
    { value: 'speeding', label: t('upload.types.speeding'), icon: '🏎️', supported: false },
    { value: 'red_light', label: t('upload.types.red_light'), icon: '📸', supported: false },
    { value: 'other', label: t('upload.types.other'), icon: '📋', supported: false },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 text-[#2D2A26]">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900 mb-4 leading-tight">
            {t('upload.title')}
          </h1>
          <p className="text-stone-500 text-lg font-light max-w-xl mx-auto leading-relaxed">
            {t('upload.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-stone-200/50 border border-stone-100 overflow-hidden relative">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-70" />
          <div className="p-8 sm:p-12 space-y-10">
            {stripeStatus === 'success' && billingActive === false && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span>Subscription not active yet, please refresh.</span>
                  <button
                    type="button"
                    onClick={() => refreshBilling(true)}
                    className="rounded-lg border border-amber-200 px-4 py-2 text-sm font-semibold text-amber-700 hover:border-amber-300"
                    disabled={billingRefreshing}
                  >
                    {billingRefreshing ? 'Refreshing...' : 'Retry'}
                  </button>
                </div>
              </div>
            )}
            {billingOverCap && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-900">
                <div className="flex flex-col gap-1">
                  <span>You’ve reached your $5 usage cap.</span>
                  <span className="text-xs text-red-700">
                    {billingCostUsd !== null && billingCapUsd !== null
                      ? `Usage: $${billingCostUsd.toFixed(2)} of $${billingCapUsd.toFixed(2)}`
                      : 'Please contact support to continue.'}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div className="space-y-4">
                <label className="block text-sm font-semibold text-stone-700 uppercase tracking-wider">
                  {t('upload.fileLabel')} <span className="text-[#C5A059]">*</span>
                </label>
                <div className="relative group">
                  <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-[#C5A059]/20 via-amber-200/20 to-[#C5A059]/20 blur opacity-0 transition duration-700 group-hover:opacity-40" />
                  <div className="relative rounded-2xl border-2 border-dashed border-[#C5A059]/30 bg-white/60 p-6 transition-all group-hover:border-[#C5A059]/60">
                    {uploadedFile ? (
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-5 w-full">
                          <div className="relative h-20 w-20 rounded-xl overflow-hidden border border-stone-200 bg-stone-100 flex-shrink-0">
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                alt="Ticket preview"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-stone-400">
                                PDF
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                              <span className="text-white text-xl">✓</span>
                            </div>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-stone-800 truncate font-display">
                              {uploadedFile.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 uppercase tracking-wide">
                                Ready
                              </span>
                              <span className="text-xs text-stone-500">
                                {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="text-sm text-red-500 hover:text-red-700 font-medium px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-1"
                        >
                          <span className="text-lg">🗑️</span>
                          <span className="hidden sm:inline">{t('upload.remove')}</span>
                        </button>
                      </div>
                    ) : (
                      <div
                        {...getRootProps()}
                        className={`rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all ${
                          billingOverCap
                            ? 'cursor-not-allowed opacity-60 border-stone-200'
                            : isDragActive
                            ? 'border-[#C5A059] bg-[#C5A059]/10 cursor-pointer'
                            : 'border-stone-200 hover:border-[#C5A059] cursor-pointer'
                        }`}
                      >
                        <input {...getInputProps()} />
                        <div className="space-y-4">
                          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto">
                            <span className="text-3xl text-[#C5A059]">☁️</span>
                          </div>
                          <div>
                            <p className="text-lg font-medium text-stone-900">
                              {isDragActive ? t('upload.drop') : t('upload.drag')}
                            </p>
                            <p className="text-sm text-stone-500">
                              <Trans i18nKey="upload.browse" ns="tickets">
                                or <span className="text-[#C5A059] font-semibold">browse files</span>
                              </Trans>
                            </p>
                            <p className="text-xs text-stone-400 mt-2">{t('upload.support')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-[#EAD9B5] bg-[#FFFCF5] p-4 text-sm text-slate-700">
                  <TermsConsentRow checked={termsChecked} onChange={setTermsChecked} />
                </div>

                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleStart}
                    disabled={
                      (!uploadedFile && !appealSessionId) ||
                      startingExtraction ||
                      billingOverCap === true ||
                      !termsChecked
                    }
                    title={startDisabledReason || undefined}
                    className="w-full relative overflow-hidden group bg-gradient-to-r from-[#C5A059] to-amber-600 hover:from-amber-400 hover:to-[#C5A059] text-white font-display font-bold text-lg py-4 rounded-xl shadow-lg shadow-[#C5A059]/30 transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {startingExtraction ? (
                      <div className="flex items-center space-x-2">
                        <LoadingSpinner size="sm" color="white" />
                        <span>{t('upload.progress.starting')}</span>
                      </div>
                    ) : (
                      t('upload.start.button')
                    )}
                  </button>
                  {startDisabledReason && (
                    <p className="mt-3 text-xs text-stone-500 text-center">
                      {startDisabledReason}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-stone-700 uppercase tracking-wider">
                  {t('upload.types.label')} <span className="text-[#C5A059]">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {ticketTypes.map((type) => {
                    const selected = watch('type') === type.value;
                    return (
                      <label
                        key={type.value}
                        className={`relative flex items-start gap-4 rounded-xl border-2 p-5 transition-all ${
                          type.supported ? 'cursor-pointer' : 'cursor-not-allowed opacity-60 grayscale-[0.4]'
                        } ${
                          selected
                            ? 'border-[#C5A059] bg-[#C5A059]/10'
                            : 'border-stone-200 hover:border-[#C5A059]/60'
                        }`}
                        aria-disabled={!type.supported}
                      >
                        <input
                          {...register('type')}
                          type="radio"
                          value={type.value}
                          className="sr-only"
                          disabled={!type.supported}
                        />
                        <span className="text-2xl">{type.icon}</span>
                        <div className="flex flex-col">
                          <span className="font-display text-lg font-semibold text-stone-800">{type.label}</span>
                          {!type.supported && (
                            <span className="text-xs text-stone-500 uppercase tracking-wide font-bold">
                              {t('upload.types.comingSoon')}
                            </span>
                          )}
                        </div>
                        {type.supported && selected && (
                          <div className="absolute top-4 right-4 h-5 w-5 rounded-full bg-[#C5A059] text-white flex items-center justify-center text-xs">
                            ✓
                          </div>
                        )}
                      </label>
                    );
                  })}
                </div>
                {errors.type && <p className="text-sm text-red-600">{errors.type.message}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-stone-700 uppercase tracking-wider">
                    {t('upload.location.label')}
                  </label>
                  <input
                    {...register('location')}
                    id="location"
                    type="text"
                    maxLength={100}
                    className={`w-full rounded-xl border-stone-300 bg-white py-3.5 pl-4 pr-4 text-sm text-stone-900 shadow-sm focus:border-[#C5A059] focus:ring focus:ring-[#C5A059]/20 ${
                      errors.location ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                    placeholder={t('upload.location.placeholder')}
                  />
                  {errors.location && <p className="text-sm text-red-600">{errors.location.message}</p>}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-stone-700 uppercase tracking-wider">
                    {t('upload.dateIssued.label')}
                  </label>
                  <input
                    {...register('dateIssued')}
                    id="dateIssued"
                    type="date"
                    className={`w-full rounded-xl border-stone-300 bg-white py-3.5 pl-4 pr-4 text-sm text-stone-500 shadow-sm focus:border-[#C5A059] focus:ring focus:ring-[#C5A059]/20 ${
                      errors.dateIssued ? 'border-red-300 focus:ring-red-500' : ''
                    }`}
                  />
                  {errors.dateIssued && <p className="text-sm text-red-600">{errors.dateIssued.message}</p>}
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-stone-700 uppercase tracking-wider">
                  {t('upload.description.label')}
                </label>
                <div className="rounded-xl p-[2px] bg-gradient-to-r from-stone-200 via-stone-300 to-stone-200 focus-within:from-[#C5A059]/40 focus-within:via-[#C5A059] focus-within:to-[#C5A059]/40 transition-all duration-500 shadow-sm">
                  <textarea
                    {...register('description')}
                    id="description"
                    rows={4}
                    maxLength={100}
                    className={`block w-full rounded-[10px] border-none bg-white p-4 text-stone-900 placeholder:text-stone-400/60 focus:ring-0 resize-none ${
                      errors.description ? 'ring-2 ring-red-200' : ''
                    }`}
                    placeholder={t('upload.description.placeholder')}
                  />
                </div>
                {errors.description && <p className="text-sm text-red-600">{errors.description.message}</p>}
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketUploadPage;
